import { defineStore } from "pinia";
import { ref } from "vue";
import axios from "axios";

export const useClientStore = defineStore("clients", () => {
  const clients = ref([]);
  const meta = ref(null);
  const isLoading = ref(false);
  const isExporting = ref(false);

  const fetchClients = async (page = 1, searchParams = {}) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const params = { page, ...searchParams };
      const response = await axios.get("/api/clients", { params });

      clients.value = response.data.data;
      meta.value = response.data.meta;
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to fetch clients";
    } finally {
      isLoading.value = false;
    }
  };

  const fetchClientById = async (id) => {
    isLoading.value = true;

    try {
      const response = await axios.get(`/api/clients/${id}`);
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to get client";
    } finally {
      isLoading.value = false;
    }
  };

  const addClient = async (clientData) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.post("/api/clients", clientData);
      clients.value.push(response.data.data);
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to add client";
    } finally {
      isLoading.value = false;
    }
  };

  const updateClient = async (id, clientData) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.put(`/api/clients/${id}`, clientData);
      const index = clients.value.findIndex((client) => client.id === id);
      if (index !== -1) {
        clients.value[index] = response.data.data;
      }
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to update client";
    } finally {
      isLoading.value = false;
    }
  };

  const deleteClient = async (id) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      await axios.delete(`/api/clients/${id}`);
      clients.value = clients.value.filter((client) => client.id !== id);
      return true;
    } catch (err) {
      throw err.response?.data?.message || "Failed to delete client";
    } finally {
      isLoading.value = false;
    }
  };

  const searchClients = async (searchParams) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.get("/api/clients/search", {
        params: searchParams,
      });
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to search clients";
    } finally {
      isLoading.value = false;
    }
  };

  // Export clients
  const exportClients = async (parameters = {}) => {
    if (isExporting.value) return;
    isExporting.value = true;

    try {
      const response = await axios.get("/api/clients/export", {
        params: parameters,
        responseType: "blob",
      });

      // Define filename directly in the frontend
      const currentDate = new Date().toISOString().slice(0, 10);
      const fileName = `clients_export_${currentDate}.xlsx`;

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting clients:", err);
    } finally {
      isExporting.value = false;
    }
  };

  return {
    clients,
    meta,
    isLoading,
    fetchClients,
    fetchClientById,
    addClient,
    updateClient,
    deleteClient,
    searchClients,
    isExporting,
    exportClients,
  };
});
